// Multiscreen showcase: turn the on-stage intel popouts into REAL OS windows the
// user can fling across monitors, and an agentic "director" that drives the whole
// thing on its own to prove this is a genuine multiscreen web application.
//
// Two capabilities live here:
//   1. tearOffToWindow(item) — reopen one popout as its own window.open() child
//      (draggable off the screen / onto another display), rendered with the same
//      intelDoc HUD document as the in-page iframe.
//   2. MultiScreenDirector — on a user gesture, enumerate the user's screens via
//      the Window Management API (getScreenDetails), open one HUD window per
//      screen, and autonomously EXPAND each to fill its display, narrating every
//      step. If there is only one screen it tiles several "virtual" windows to
//      showcase the same behaviour; if pop-ups are blocked it falls back to an
//      in-page simulated video wall so the demo always shows something.

import { popoutDocHtml } from "./intelDoc";
import { nextIntel, type IntelItem } from "../vision/intel";
import type { Speaker } from "./popouts";

// Deterministic ids only — Date.now()/Math.random() are banned in this codebase.
let seq = 0;

function writeDoc(win: Window, item: IntelItem, fullscreen: boolean, label: string): void {
  try {
    win.document.open();
    win.document.write(popoutDocHtml(item, { fullscreen, label }));
    win.document.close();
    win.document.title = `${item.tag} · ${label}`;
  } catch {
    // Cross-origin or closed window — ignore; the window still exists visually.
  }
}

/**
 * Reopen an intel item as its own browser window. Returns the Window, or null if
 * the browser blocked the pop-up (caller should keep the on-stage panel).
 */
export function tearOffToWindow(item: IntelItem, size?: { w: number; h: number }): Window | null {
  const w = Math.round(size?.w ?? 360);
  const h = Math.round(size?.h ?? 230);
  // Centre on the current screen's available area.
  const sx = (screen as Screen & { availLeft?: number }).availLeft ?? 0;
  const sy = (screen as Screen & { availTop?: number }).availTop ?? 0;
  const left = Math.round(sx + (screen.availWidth - w) / 2);
  const top = Math.round(sy + (screen.availHeight - h) / 2);
  const feats = `popup,noopener=no,width=${w},height=${h},left=${left},top=${top}`;
  const win = window.open("", `intel_${seq++}`, feats);
  if (!win) return null;
  writeDoc(win, item, false, "TORN-OFF NODE");
  return win;
}

interface ScreenBox {
  x: number;
  y: number;
  w: number;
  h: number;
  label: string;
  primary: boolean;
}

interface Node {
  win: Window;
  target: ScreenBox;
}

const lerp = (a: number, b: number, t: number): number => Math.round(a + (b - a) * t);

/** The agentic multiscreen demo. Construct once; call run() from a click. */
export class MultiScreenDirector {
  private nodes: Node[] = [];
  private timers: number[] = [];
  private console: HTMLElement | null = null;
  private running = false;

  constructor(private speaker: Speaker) {}

  get active(): boolean {
    return this.running;
  }

  /** Kick off the demo. MUST be called from within a user gesture (pop-up rule). */
  async run(): Promise<void> {
    if (this.running) {
      this.stop();
      return;
    }
    this.running = true;
    this.mountConsole();
    this.log("INITIALISING MULTISCREEN NODE ARRAY…", true);

    // Open the windows SYNCHRONOUSLY, still inside the click's user-activation,
    // so the pop-up blocker lets them all through. We over-provision up to a cap
    // and reconcile against the real screen count once it resolves.
    const CAP = 4;
    const opened: Window[] = [];
    for (let i = 0; i < CAP; i++) {
      const w = window.open("", `msnode_${seq++}`, "popup,width=380,height=240,left=60,top=60");
      if (w) opened.push(w);
    }

    if (opened.length === 0) {
      this.log("POP-UPS BLOCKED → simulating a video wall on this screen.", true);
      this.runInPageWall(CAP);
      return;
    }

    const screens = await this.detectScreens();
    const targets = this.planTargets(screens, opened.length);
    this.log(`${screens.length} physical screen(s) detected · placing ${targets.length} node(s).`, true);

    // Assign windows to targets; close any surplus windows we opened.
    for (let i = 0; i < opened.length; i++) {
      if (i < targets.length) {
        this.nodes.push({ win: opened[i]!, target: targets[i]! });
      } else {
        try { opened[i]!.close(); } catch { /* ignore */ }
      }
    }

    // Seed content, then agentically expand each node to fill its screen, one at
    // a time so the narration reads like a sequence of deliberate placements.
    this.nodes.forEach((n, i) => {
      const item = nextIntel() ?? FALLBACK;
      writeDoc(n.win, item, true, n.target.label);
      this.timers.push(
        window.setTimeout(() => this.expand(n, i + 1), 500 + i * 900),
      );
    });

    // Once placed, keep the wall alive: rotate fresh intel through the windows.
    this.timers.push(window.setInterval(() => this.rotate(), 4200));
    // Any node the user closes should retire quietly; poll for that.
    this.timers.push(window.setInterval(() => this.reap(), 1500));
  }

  /** Tear everything down and close all child windows. */
  stop(): void {
    this.running = false;
    for (const t of this.timers) { clearTimeout(t); clearInterval(t); }
    this.timers = [];
    for (const n of this.nodes) { try { n.win.close(); } catch { /* ignore */ } }
    this.nodes = [];
    document.getElementById("ms-wall")?.remove();
    if (this.console) {
      this.log("ARRAY COLLAPSED.", true);
      const c = this.console;
      window.setTimeout(() => c.remove(), 1200);
      this.console = null;
    }
  }

  // --- screen enumeration -------------------------------------------------

  private async detectScreens(): Promise<ScreenBox[]> {
    const w = window as unknown as { getScreenDetails?: () => Promise<any> };
    if (typeof w.getScreenDetails === "function") {
      try {
        const details = await w.getScreenDetails();
        const list: ScreenBox[] = details.screens.map((s: any, i: number) => ({
          x: s.availLeft ?? s.left ?? 0,
          y: s.availTop ?? s.top ?? 0,
          w: s.availWidth ?? s.width,
          h: s.availHeight ?? s.height,
          label: s.label || `SCREEN ${i + 1}`,
          primary: !!s.isPrimary,
        }));
        if (list.length) return list;
      } catch {
        // permission denied / unsupported → fall through to single-screen
      }
    }
    const s = screen as Screen & { availLeft?: number; availTop?: number };
    return [{
      x: s.availLeft ?? 0,
      y: s.availTop ?? 0,
      w: screen.availWidth,
      h: screen.availHeight,
      label: "PRIMARY",
      primary: true,
    }];
  }

  /**
   * Decide where each node should end up. Two physical screens → one full-screen
   * node per display. One screen → tile it into quadrants so the "expand to fill
   * your screen" behaviour is still on show (labelled as virtual sub-screens).
   */
  private planTargets(screens: ScreenBox[], have: number): ScreenBox[] {
    if (screens.length >= 2) {
      return screens.slice(0, have).map((s, i) => ({
        ...s,
        label: `SCREEN ${i + 1} · ${s.label}${s.primary ? " (primary)" : ""} · ${s.w}×${s.h}`,
      }));
    }
    // Single screen: quadrant tiling.
    const s = screens[0]!;
    const n = Math.min(have, 4);
    const cols = n <= 1 ? 1 : 2;
    const rows = n <= 2 ? 1 : 2;
    const tw = Math.floor(s.w / cols);
    const th = Math.floor(s.h / rows);
    const out: ScreenBox[] = [];
    for (let i = 0; i < n; i++) {
      const cx = i % cols;
      const cy = Math.floor(i / cols);
      out.push({
        x: s.x + cx * tw,
        y: s.y + cy * th,
        w: tw,
        h: th,
        label: `VIRTUAL SCREEN ${i + 1} · ${tw}×${th}`,
        primary: i === 0,
      });
    }
    return out;
  }

  // --- animation ----------------------------------------------------------

  /** Smoothly grow a node from wherever it is to fill its whole target screen. */
  private expand(node: Node, idx: number): void {
    if (!this.running) return;
    const t = node.target;
    this.log(`NODE ${idx} → ${t.label} — EXPANDING TO FIT`, true);
    let sx0 = 60, sy0 = 60, sw0 = 380, sh0 = 240;
    try { sx0 = node.win.screenX; sy0 = node.win.screenY; sw0 = node.win.outerWidth; sh0 = node.win.outerHeight; } catch { /* ignore */ }
    const STEPS = 18;
    let k = 0;
    const step = window.setInterval(() => {
      if (!this.running) { clearInterval(step); return; }
      k++;
      const p = k / STEPS;
      // ease-out cubic
      const e = 1 - Math.pow(1 - p, 3);
      try {
        node.win.moveTo(lerp(sx0, t.x, e), lerp(sy0, t.y, e));
        node.win.resizeTo(lerp(sw0, t.w, e), lerp(sh0, t.h, e));
      } catch { /* browser may clamp cross-screen moves without permission */ }
      if (k >= STEPS) {
        clearInterval(step);
        try { node.win.moveTo(t.x, t.y); node.win.resizeTo(t.w, t.h); } catch { /* ignore */ }
      }
    }, 26);
    this.timers.push(step);
  }

  private rotate(): void {
    if (!this.running || this.nodes.length === 0) return;
    const i = seq % this.nodes.length;
    const node = this.nodes[i]!;
    if (node.win.closed) return;
    const item = nextIntel() ?? FALLBACK;
    writeDoc(node.win, item, true, node.target.label);
    this.speaker.speak(item.title);
  }

  private reap(): void {
    const before = this.nodes.length;
    this.nodes = this.nodes.filter((n) => !n.win.closed);
    if (this.nodes.length !== before) this.log(`node closed — ${this.nodes.length} live.`);
    if (this.running && this.nodes.length === 0) this.stop();
  }

  // --- in-page fallback (pop-ups blocked) ---------------------------------

  private runInPageWall(count: number): void {
    const wall = document.createElement("div");
    wall.id = "ms-wall";
    document.body.appendChild(wall);
    const cols = count <= 1 ? 1 : 2;
    const rows = count <= 2 ? 1 : 2;
    for (let i = 0; i < count; i++) {
      const item = nextIntel() ?? FALLBACK;
      const cell = document.createElement("iframe");
      cell.className = "ms-cell";
      cell.setAttribute("sandbox", "allow-scripts");
      cell.srcdoc = popoutDocHtml(item, { fullscreen: true, label: `VIRTUAL SCREEN ${i + 1}` });
      const cx = i % cols, cy = Math.floor(i / cols);
      // Start tiny in the centre, then expand to its grid cell.
      cell.style.left = "50%"; cell.style.top = "50%";
      cell.style.width = "8vw"; cell.style.height = "6vh";
      cell.style.transform = "translate(-50%,-50%)";
      wall.appendChild(cell);
      window.setTimeout(() => {
        cell.style.left = `${(cx / cols) * 100}%`;
        cell.style.top = `${(cy / rows) * 100}%`;
        cell.style.width = `${100 / cols}vw`;
        cell.style.height = `${100 / rows}vh`;
        cell.style.transform = "none";
      }, 120 + i * 260);
    }
    this.log(`SIMULATED WALL · ${count} virtual screens. Allow pop-ups for real windows.`, true);
    this.timers.push(window.setInterval(() => {
      const cells = wall.querySelectorAll<HTMLIFrameElement>(".ms-cell");
      const c = cells[seq % Math.max(1, cells.length)];
      if (c) { const it = nextIntel() ?? FALLBACK; c.srcdoc = popoutDocHtml(it, { fullscreen: true, label: c.srcdoc.includes("VIRTUAL") ? "VIRTUAL SCREEN" : "NODE" }); this.speaker.speak(it.title); }
    }, 4200));
  }

  // --- director console ---------------------------------------------------

  private mountConsole(): void {
    const el = document.createElement("div");
    el.id = "ms-console";
    el.innerHTML = `
      <div class="ms-head"><span class="ms-dot"></span> MULTISCREEN DIRECTOR
        <button id="ms-stop" title="collapse the array">✕ stop</button></div>
      <div class="ms-log" id="ms-log"></div>`;
    document.body.appendChild(el);
    el.querySelector<HTMLButtonElement>("#ms-stop")!.addEventListener("click", () => this.stop());
    this.console = el;
  }

  private log(line: string, speak = false): void {
    const box = document.getElementById("ms-log");
    if (box) {
      const row = document.createElement("div");
      row.className = "ms-line";
      row.textContent = `▸ ${line}`;
      box.appendChild(row);
      box.scrollTop = box.scrollHeight;
      while (box.childElementCount > 40) box.firstElementChild?.remove();
    }
    if (speak) this.speaker.speak(line);
  }
}

// Never leave a node blank if intel has not finished loading yet.
const FALLBACK: IntelItem = {
  kind: "readout",
  tag: "NODE",
  title: "MULTISCREEN NODE ONLINE",
  text: "This window is a live surface of the same web app, placed on your display by the app itself.",
  source: "lietome // window-management API",
};
